import {
    doesScoreExist,
    ScoreDimensionValue,
    Score,
    Scores,
    ScoreValue,
    ScoreWithStatistics,
    ScoreSkeleton,
} from "src/api/client-api/post/scoreSchema";
import { ScoreImprovement, Score as SureScore } from "src/types";
import { LastScoreSelection } from "./useVariations";
import { DEFAULT_NUMBER_PRECISION } from "../NumberFormat";

export type ScoreVariation = {
    rowId: number;
    label: string;
    group: string;
    scoreId: number;
    variationId: number | null;
    unit: string;
    onClick: () => void;
    precision: number;
    improvement: ScoreImprovement;
    disabled?: boolean;
};

export type ScoreByProperties<ScoreSkeleton = ScoreValue> = Partial<Record<string, ScoreDimensionValue<ScoreSkeleton>>>;

export type ScoreDetails = {
    scoresWithVariations: ScoreVariation[];
    lastSelected: ScoreVariation;
};

export class ScoreService {
    public static getScoreList(
        scoresEnabled: SureScore[],
        entityScores: Scores | undefined,
        scoreAction?: (scoreId: number, scoreVariationId: number | null) => void,
        lastSelectedVariation?: LastScoreSelection | null,
    ): ScoreDetails {
        const scoresWithVariations = ScoreService.getAllScoreVariations(scoresEnabled, entityScores, scoreAction);
        const lastSelected =
            lastSelectedVariation === undefined || lastSelectedVariation === null ?
                scoresWithVariations[0]
            :   (scoresWithVariations.find((score) => {
                    const doesScoreMatch = score.scoreId === lastSelectedVariation.scoreId;
                    const doesVariationMatch = score.variationId === lastSelectedVariation.scoreVariationId;
                    return doesScoreMatch && doesVariationMatch;
                }) ?? scoresWithVariations[0]);

        return {
            scoresWithVariations,
            lastSelected,
        };
    }

    public static getAllScoreVariations(
        scoresEnabled: SureScore[],
        entityScores: Scores | undefined,
        scoreAction?: (scoreId: number, scoreVariationId: number | null) => void,
    ): ScoreVariation[] {
        let currentRowID = 0;
        const allScoreWithVariations = scoresEnabled.flatMap((score) => {
            const rowId = currentRowID;
            currentRowID += 1;

            const scoreOnlyInformation = {
                scoreId: score.id,
                group: score.title,
                precision: score.precision ?? DEFAULT_NUMBER_PRECISION,
                improvement: score.improvement,
            };

            const firstScoreBlock: ScoreVariation = {
                rowId: rowId,
                variationId: null,
                label: score.label ?? "",
                unit: score.unit?.abbreviation ?? "",
                onClick: () => {
                    scoreAction?.(score.id, null);
                },

                ...scoreOnlyInformation,
            };

            const entityScore = entityScores?.[score.id];
            const entityVariations = entityScore?.variations ?? [];
            const scoreVariations: ScoreVariation[] = entityVariations.map((scoreVariation) => {
                const variationRowID = currentRowID;
                currentRowID += 1;

                return {
                    rowId: variationRowID,
                    variationId: scoreVariation.id,
                    label: scoreVariation.label,
                    unit: scoreVariation.unit,
                    onClick: () => {
                        scoreAction?.(score.id, scoreVariation.id);
                    },

                    ...scoreOnlyInformation,
                };
            });
            const opts = [firstScoreBlock, ...scoreVariations];
            return opts;
        });

        return allScoreWithVariations;
    }

    public static getScoreProperties = (scoreId: number | undefined, scores: Score | undefined): ScoreByProperties => {
        // const scores = scores?.[scoreId];
        if (scores !== undefined && doesScoreExist(scores)) {
            const foundProperties = scores.dimensions.find((dimension) => dimension.label === "properties");
            if (foundProperties !== undefined) {
                return foundProperties.values;
            }
        }

        return {};
    };

    public static substractScore(startScore: number | null, endScore: number | null): number | null {
        if (endScore === null && startScore === null) {
            return null;
        }

        if (endScore === null && startScore !== null) {
            // Return * -1 ?
            // To negate it, and say :
            // if my start is 10, and current is unknown, then diff is -10;
            return startScore;
        }

        if (endScore !== null && startScore === null) {
            // Weird case, but eh.
            return endScore;
        }

        if (endScore !== null && startScore !== null) {
            return endScore - startScore;
        }

        return null;
    }

    public static addScore(base: number | null, toAdd: number | null): number | null {
        if (toAdd === null && base === null) {
            return null;
        }

        if (toAdd === null && base !== null) {
            return base;
        }

        if (toAdd !== null && base === null) {
            return toAdd;
        }

        if (toAdd !== null && base !== null) {
            return toAdd + base;
        }

        return null;
    }

    public static normalizeScore(value: ScoreSkeleton | undefined, precision: number): ScoreWithStatistics {
        if (value === null || value === undefined) {
            return {
                value: null,
                prev: null,
                avg: null,
                min: null,
                max: null,
                count: null,
            };
        }

        if (typeof value === "number") {
            const formattedValue = Number(value.toFixed(precision));
            return {
                value: formattedValue,
                prev: null,
                avg: null,
                min: null,
                max: null,
                count: null,
            };
        }

        const { value: _value, avg, min, max, count, prev } = value;

        return {
            value: _value === null ? null : Number(_value.toFixed(precision)),
            prev: prev === null ? null : Number(prev.toFixed(precision)),
            avg: avg === null ? null : Number(avg.toFixed(precision)),
            min: min === null ? null : Number(min.toFixed(precision)),
            max: max === null ? null : Number(max.toFixed(precision)),
            count: count === null ? null : Number(count),
        };
    }
}
